import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { XStack } from 'tamagui';
import { GBAlert, GBButton } from '@greenbackapp/ui';
import { BusinessBillingAlertRail, isInvoiceAlert } from '../../../types/platformBilling';
import { readOnlyReason } from './readOnlyReason';
import { formatInvoiceDate, lastWritableDay } from '../settings/billing/invoiceFormat';
import { BUSINESS_START_PATH } from '../../auth/paywallScope';
import { useBusinessOnSale } from '../../../hooks/usePublicPricing';

// Entitlement banners for the Business SKU model (IN_PLACE_ADAPTATION.md
// "Monetization"): flag = deployed, SKU = entitled.
//  - BusinessUpsellBanner: teaser mode — flag on, no businesses, not entitled.
//  - ReadOnlyBusinessBanner: the active business is read-only; it says
//    "lapsed" only when the billing alert does (or to an owner it does not
//    explain), and hands a pause to the banner below.
//  - PausedSubscriptionBanner: read-only because the group's own card
//    subscription is paused in Stripe, shown in place of the lapsed banner.
//
// GREENBACK'S OWN INVOICE (PRD 112 Wave 2). An alert read from an invoice row
// carries `rail: "invoice"` (and the past-due invoice's number). There is no
// card to update and nothing to resubscribe to: the owner pays the invoice
// from their bank. So those banners name the invoice, say when the books turn
// read-only, and send the owner to the billing card (View billing, View
// invoice), never to card checkout, the portal or "Update card". A cancelled
// invoice row that has ended says it was cancelled, not unpaid. A card alert
// carries no `rail` and reads exactly as before.
//
// EVERY CTA IS A REAL BUTTON, drawn under the alert. GBAlert's `action` is a
// bare pressable View in the kit, with no role and no tab stop, so a keyboard
// could never reach "Update card" (PRD 114 §4.5); only the jest mock made it a
// <button>. Where an owner's button goes is the shell's to say: Manage
// subscription once it knows the group, the billing card before.

const BannerWrap = styled.div`
  margin: 16px 24px 0;
`;

/** The banner's one action, as a button a keyboard reaches. */
const BannerAction: React.FC<{ label: string; onPress: () => void }> = ({ label, onPress }) => (
  <XStack marginTop="$2">
    <GBButton variant="outline" size="sm" onPress={onPress}>{label}</GBButton>
  </XStack>
);

/**
 * The subscription surface, with a product hint for future deep-linking to
 * the Business SKU's packages.
 */
export const BUSINESS_SUBSCRIBE_PATH = '/subscribe?product=business';

/**
 * Teaser-mode banner: the user sees the sample-data preview and can subscribe
 * to unlock live mode. Replaces PreviewBanner for unentitled users.
 *
 * While Greenback Business is on sale, Subscribe opens /business/start, where
 * it is bought (PRD 86 slice 5). Until the price list confirms that, it keeps
 * its old destination: it fails closed.
 */
export const BusinessUpsellBanner: React.FC = () => {
  const navigate = useNavigate();
  const target = useBusinessOnSale() ? BUSINESS_START_PATH : BUSINESS_SUBSCRIBE_PATH;
  return (
    <BannerWrap>
      <GBAlert
        variant="info"
        title="Greenback Business preview"
        message="You're exploring a preview with sample data. Subscribe to Greenback Business to connect your real accounts."
      />
      <BannerAction label="Subscribe to Greenback Business" onPress={() => navigate(target)} />
    </BannerWrap>
  );
};

interface ReadOnlyBusinessBannerProps {
  /** Owners get a Resubscribe CTA; members just see the explanation. */
  isOwner: boolean;
  /**
   * The billing alert the api sent THIS viewer, if any. It decides the
   * wording: "lapsed" only when it says lapsed (or for an owner it does not
   * explain), the paused banner when it says paused, neutral otherwise.
   */
  alert?: BusinessBillingAlertRail | null;
  /**
   * Where an owner manages a subscription that still exists (paused, or a
   * card to fix): Manage subscription, deep-linked to the group, once the
   * shell knows it; the billing card before. For an invoice alert, the
   * billing card, where the invoices are.
   */
  onManageBilling?: () => void;
}

/**
 * Persistent read-only banner: shown on every /business/* page while the
 * active business is in read_only mode. Books are never held hostage — reads,
 * reports and exports keep working; only mutations are blocked.
 *
 * It says WHY only when it knows (readOnlyReason). Bookkeepers, clerks,
 * read-only members and field roles were told "the subscription has lapsed"
 * for a group that was only paused; a member nobody told why is now told the
 * books are read-only and no more.
 */
export const ReadOnlyBusinessBanner: React.FC<ReadOnlyBusinessBannerProps> = ({
  isOwner, alert = null, onManageBilling,
}) => {
  const navigate = useNavigate();
  const reason = readOnlyReason(isOwner, alert);

  // One banner for read-only, never two: the paused one takes this one's place.
  if (reason === 'paused' && alert) {
    return <PausedSubscriptionBanner alert={alert} onManage={onManageBilling} />;
  }

  // Greenback's invoice is unpaid past its window, or billing was cancelled.
  // Never Resubscribe: there is no card subscription to buy.
  if (reason === 'lapsed' && alert && isInvoiceAlert(alert)) {
    const message = alert.cancelled
      ? (isOwner
        ? 'Billing for this business was cancelled, so it is read-only. Restart billing from Subscription & billing.'
        : 'Billing for this business was cancelled, so it is read-only. The owner can restart billing from Subscription & billing.')
      : 'The Greenback invoice for this business is unpaid, so it is read-only. Books, reports and exports remain available; paying restores everything.';
    return (
      <BannerWrap>
        <GBAlert variant="warning" title="This business is read-only" message={message} />
        {isOwner && onManageBilling && <BannerAction label="View billing" onPress={onManageBilling} />}
      </BannerWrap>
    );
  }

  if (reason === 'lapsed') {
    return (
      <BannerWrap>
        <GBAlert
          variant="warning"
          title="This business is read-only"
          message="The owner's Greenback Business subscription has lapsed. Books, reports, and exports remain available."
        />
        {isOwner && <BannerAction label="Resubscribe" onPress={() => navigate(BUSINESS_SUBSCRIBE_PATH)} />}
      </BannerWrap>
    );
  }

  // Not told why. No Resubscribe either: for an owner this is an alert that
  // is neither lapsed nor paused, so a subscription still exists, and buying a
  // second one is not the fix; billing is where it is managed.
  return (
    <BannerWrap>
      <GBAlert
        variant="warning"
        title="This business is read-only"
        message={
          isOwner
            ? "Changes can't be saved until the Greenback Business subscription is active. Books, reports, and exports remain available."
            : "Changes can't be saved until the owner's Greenback Business subscription is active. Books, reports, and exports remain available."
        }
      />
      {isOwner && onManageBilling && <BannerAction label="Manage billing" onPress={onManageBilling} />}
    </BannerWrap>
  );
};

interface PausedSubscriptionBannerProps {
  alert: BusinessBillingAlertRail;
  /** Owners are sent to Manage subscription to resume it; an accountant is told, not handed the portal. */
  onManage?: () => void;
}

/**
 * Read-only because the group's own card subscription is PAUSED in Stripe,
 * not because it lapsed. It replaces ReadOnlyBusinessBanner rather than sitting
 * beside it: "lapsed / Resubscribe" would be false, and would point the owner
 * at buying a second subscription beside the one that is only paused.
 *
 * Persistent, like the banner it replaces: the books stay read-only for as long
 * as the pause lasts. Nothing for any other status.
 */
export const PausedSubscriptionBanner: React.FC<PausedSubscriptionBannerProps> = ({
  alert, onManage,
}) => {
  if (alert.status !== 'paused') return null;

  return (
    <BannerWrap>
      <GBAlert
        variant="warning"
        title="Subscription paused"
        message={
          alert.can_fix
            ? 'Your Business subscription is paused, so the books are read-only until it resumes. Books, reports and exports remain available.'
            : "The owner's Business subscription is paused, so the books are read-only until it resumes. Books, reports and exports remain available."
        }
      />
      {alert.can_fix && onManage && <BannerAction label="Manage billing" onPress={onManage} />}
    </BannerWrap>
  );
};

interface PaymentDeclinedBannerProps {
  businessId: string;
  alert: BusinessBillingAlertRail;
  /**
   * Owners are sent to fix the card; an accountant is told, not handed a card.
   * For an invoice alert, the billing card, where the invoice is.
   */
  onFix?: () => void;
}

/**
 * Dismissed per SESSION, and re-shown when the situation changes.
 *
 * A banner dismissed once and gone for ever is worse than none: somebody
 * clears it on day one and discovers the problem on day twenty-one, when their
 * books stop accepting entries. So the key includes the status and the urgency
 * — dismissing the calm version does not suppress the countdown, and a fresh
 * decline after a recovery re-shows.
 */
function dismissKey(businessId: string, alert: BusinessBillingAlertRail): string {
  return `gb.billingAlert.dismissed:${businessId}:${alert.status}:${alert.urgent}`;
}

function readDismissed(key: string): boolean {
  try {
    return window.sessionStorage.getItem(key) === '1';
  } catch {
    // Private windows and blocked site data throw rather than returning null.
    return false;
  }
}

/**
 * A failed payment, while the customer can still work.
 *
 * Deliberately NOT rendered for `lapsed`: at that point the business is
 * read-only and ReadOnlyBusinessBanner already says so — two warnings about
 * one problem is how people learn to ignore both. Nor for `paused`, for the
 * same reason: PausedSubscriptionBanner says that.
 */
export const PaymentDeclinedBanner: React.FC<PaymentDeclinedBannerProps> = ({
  businessId, alert, onFix,
}) => {
  const key = dismissKey(businessId, alert);
  const [dismissed, setDismissed] = React.useState(() => readDismissed(key));

  // A change in status or urgency is a different banner, so it comes back.
  React.useEffect(() => { setDismissed(readDismissed(key)); }, [key]);

  if (alert.status !== 'past_due' || dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      window.sessionStorage.setItem(key, '1');
    } catch {
      // Nothing to do: it simply shows again on the next render.
    }
  };

  // Greenback's own invoice, past its due date: the owner pays it from their
  // bank, so the banner names it and the last writable day, and offers the
  // invoice, never a card.
  if (isInvoiceAlert(alert)) {
    const lastDay = formatInvoiceDate(lastWritableDay(alert.suspends_on));
    const when = lastDay
      ? `The books become read-only after ${lastDay}.`
      : 'The books become read-only if it stays unpaid.';
    return (
      <BannerWrap>
        <GBAlert
          variant={alert.urgent ? 'error' : 'warning'}
          title={alert.invoice_number ? `Invoice ${alert.invoice_number} is past due` : 'A Greenback invoice is past due'}
          message={
            alert.can_fix
              ? `${when} Books, reports and exports keep working until then.`
              : `${when} The owner receives the invoices.`
          }
          dismissible
          onDismiss={dismiss}
        />
        {alert.can_fix && onFix && <BannerAction label="View invoice" onPress={onFix} />}
      </BannerWrap>
    );
  }

  const days = alert.days_remaining;
  const countdown = days === null
    ? 'Your books stay available while it retries.'
    : days <= 0
      ? 'This business becomes read-only imminently.'
      : `${days} ${days === 1 ? 'day' : 'days'} until this business becomes read-only.`;

  return (
    <BannerWrap>
      <GBAlert
        variant={alert.urgent ? 'error' : 'warning'}
        title={alert.urgent ? 'Payment still failing' : 'A payment failed'}
        message={
          alert.can_fix
            ? `${countdown} Update the card to clear it — books, reports and exports keep working until then.`
            : `${countdown} The account owner needs to update the card.`
        }
        dismissible
        onDismiss={dismiss}
      />
      {alert.can_fix && onFix && <BannerAction label="Update card" onPress={onFix} />}
    </BannerWrap>
  );
};
